import { useState, useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { useStudyQueriesList } from './useStudyQueriesList'
import { useStudyQueryMetadata } from './useStudyQueriesMetadata'
import { useCreateStudyQuery } from './useCreateStudyQuery'
import { useUpdateStudyQueryStatus } from './useUpdateStudyQueryStatus'
import { useAddQueryMessage } from './useAddStudyQueryMessage'
import { useUpdateStudyQuery } from './useUpdateStudyQuery'
import { useStudyQueryDetails } from './useStudyQueryDetails'
import {
  StudyQueryUpdateSchema,
  AddQueryMessageStudiesStudyIdQueriesStudyQueryIdMessagesPostData,
  UpdateStudyQueryStatusStudiesStudyIdQueriesStudyQueryIdUpdateStatusPatchData,
  UpdateStudyQueryStudiesStudyIdQueriesStudyQueryIdPatchData,
  CreateStudyQueryStudiesStudyIdQueriesPostData,
  QueryStatus_Input,
} from '@common/config/api/client'
import { QueryListParams } from '../types/types'
import { handleMutationError } from '@common/hooks/handleMutationError'
import { toast } from 'sonner'
import Toast from '@common/components/Toast'
import { CreateQueryFormData } from '../schemas/queryValidationSchema'

export function useQueriesManagementHandlers() {
  const { studyId } = useParams<{ studyId: string }>()

  const [page, setPage] = useState(0)
  const [queryListParams, setQueryListParams] = useState<QueryListParams>({
    study_id: studyId!,
    limit: 50,
    order_by_field: 'created_at',
    asc_order: false,
  })
  const [selected, setSelected] = useState<string[]>([])
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false)
  const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false)
  const [selectedQueryId, setSelectedQueryId] = useState<string | null>(null)

  useEffect(() => {
    setQueryListParams((prev) => ({ ...prev, study_id: studyId!, next_cursor: undefined, previous_cursor: undefined }))
    setPage(0)
    setSelectedQueryId(null)
    setIsViewDrawerOpen(false)
    setIsCreateDrawerOpen(false)
  }, [studyId])

  const {
    data: queriesResponse,
    isLoading: isLoadingList,
    isError: isErrorList,
  } = useStudyQueriesList(queryListParams, {
    enabled: !!studyId,
  })

  const { data: metadata, isLoading: isLoadingMetadata } = useStudyQueryMetadata(
    { study_id: studyId! },
    { enabled: !!studyId && isCreateDrawerOpen },
  )

  const {
    data: queryDetailsResponse,
    isLoading: isLoadingDetails,
    isError: isErrorDetails,
  } = useStudyQueryDetails(
    { studyId: studyId!, queryId: selectedQueryId! },
    { enabled: !!studyId && !!selectedQueryId && isViewDrawerOpen },
  )
  const selectedQueryDetails = queryDetailsResponse?.data

  const createMutation = useCreateStudyQuery()
  const updateStatusMutation = useUpdateStudyQueryStatus()
  const addMessageMutation = useAddQueryMessage()
  const updateQueryMutation = useUpdateStudyQuery()

  const handlePageChange = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      const isNextPage = newPage > page
      const cursorKey = isNextPage ? 'next_cursor' : 'previous_cursor'
      const cursor = queriesResponse?.pagination[cursorKey]

      if ((cursor !== undefined && cursor !== null) || (!isNextPage && newPage >= 0)) {
        setQueryListParams((prev) => ({
          ...prev,
          next_cursor: isNextPage ? (cursor ?? undefined) : undefined,
          previous_cursor: !isNextPage ? (cursor ?? undefined) : undefined,
        }))
        setPage(newPage)
      } else {
        console.warn(`Cannot navigate ${isNextPage ? 'next' : 'previous'}: cursor is missing or invalid page.`)
      }
    },
    [page, queriesResponse?.pagination],
  )

  const handleRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setQueryListParams((prev) => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      next_cursor: undefined,
      previous_cursor: undefined,
    }))
    setPage(0)
  }, [])

  const handleSearch = useCallback((value: string) => {
    const handler = setTimeout(() => {
      setQueryListParams((prev) => ({
        ...prev,
        search: value || undefined,
        next_cursor: undefined,
        previous_cursor: undefined,
      }))
      setPage(0)
    }, 500)
    return () => clearTimeout(handler)
  }, [])

  const handleRequestSort = useCallback(
    (_event: React.MouseEvent<unknown>, property: string) => {
      const isCurrentlyAsc = queryListParams.order_by_field === property && queryListParams.asc_order
      setQueryListParams((prev) => ({
        ...prev,
        order_by_field: property,
        asc_order: !isCurrentlyAsc,
        next_cursor: undefined,
        previous_cursor: undefined,
      }))
      setPage(0)
    },
    [queryListParams.order_by_field, queryListParams.asc_order],
  )

  const handleCreateQueryClick = useCallback(() => {
    setIsCreateDrawerOpen(true)
  }, [])

  const handleCloseCreateDrawer = useCallback(() => {
    setIsCreateDrawerOpen(false)
  }, [])

  const handleSubmitCreateQuery = useCallback(
    (formData: CreateQueryFormData) => {
      const {
        study_qc_task_id,
        study_site_id,
        study_timepoint_submission_id,
        category,
        study_timepoint_container_submission_ids,
        query_assignee_ids,
        cc_user_ids,
        initial_message_title,
        initial_message_body,
        requires_timepoint_resubmission,
      } = formData
      const mutationVariables: CreateStudyQueryStudiesStudyIdQueriesPostData = {
        path: { study_id: studyId! },
        body: {
          study_qc_task_id: study_qc_task_id || null,
          study_site_id,
          study_timepoint_submission_id,
          category,
          study_timepoint_container_submission_ids,
          query_assignee_ids,
          cc_user_ids: cc_user_ids ?? [],
          initial_message_title,
          initial_message_body,
          requires_timepoint_resubmission,
        },
      }
      createMutation.mutate(mutationVariables, {
        onSuccess: () => {
          handleCloseCreateDrawer()
          setQueryListParams((prev) => ({ ...prev, next_cursor: undefined, previous_cursor: undefined }))
          setPage(0)
          toast(<Toast variant="success" message="Study query created successfuly." />)
        },
        onError: handleMutationError,
      })
    },
    [studyId, createMutation, handleCloseCreateDrawer],
  )

  const handleViewQueryClick = useCallback((queryId: string) => {
    setSelectedQueryId(queryId)
    setIsViewDrawerOpen(true)
  }, [])

  const handleCloseViewDrawer = useCallback(() => {
    setIsViewDrawerOpen(false)
    setSelectedQueryId(null)
  }, [])

  const handleUpdateStatus = useCallback(
    (
      queryId: string,
      status: QueryStatus_Input,
      reason?: string,
      containerIds?: string[],
      requiresTimepointResubmission?: boolean,
    ) => {
      const mutationVariables: UpdateStudyQueryStatusStudiesStudyIdQueriesStudyQueryIdUpdateStatusPatchData = {
        path: { study_id: studyId!, study_query_id: queryId },
        body: {
          status,
          reason,
          requires_timepoint_resubmission: requiresTimepointResubmission,
          ...(containerIds && { container_ids: containerIds }),
        },
      }
      updateStatusMutation.mutate(mutationVariables, {
        onError: handleMutationError,
        onSuccess: () => {
          toast(<Toast variant="success" message="Status updated successfully." />)
          setIsViewDrawerOpen(false)
        },
      })
    },
    [studyId, updateStatusMutation, selectedQueryId, isViewDrawerOpen],
  )

  const handleSendMessage = useCallback(
    (queryId: string, message: string, title?: string) => {
      const mutationVariables: AddQueryMessageStudiesStudyIdQueriesStudyQueryIdMessagesPostData = {
        path: { study_id: studyId!, study_query_id: queryId },
        body: { message, title },
      }
      addMessageMutation.mutate(mutationVariables, {
        onError: handleMutationError,
      })
    },
    [studyId, addMessageMutation, selectedQueryId, isViewDrawerOpen],
  )

  const handleUpdateQuery = useCallback(
    (queryId: string, updatedData: StudyQueryUpdateSchema) => {
      const mutationVariables: UpdateStudyQueryStudiesStudyIdQueriesStudyQueryIdPatchData = {
        path: { study_id: studyId!, study_query_id: queryId },
        body: updatedData,
      }
      updateQueryMutation.mutate(mutationVariables, {
        onError: handleMutationError,
        onSuccess: () => {
          setIsViewDrawerOpen(false)
          toast(<Toast variant="success" message="Study query updated successfully." />)
        },
      })
    },
    [studyId, updateQueryMutation, selectedQueryId, isViewDrawerOpen],
  )

  return {
    page,
    queryListParams,
    setQueryListParams,
    selected,
    setSelected,
    isCreateDrawerOpen,
    handleCreateQueryClick,
    handleCloseCreateDrawer,
    handleSubmitCreateQuery,
    isCreating: createMutation.isPending,
    metadata,
    isLoadingMetadata,
    isViewDrawerOpen,
    selectedQueryId,
    selectedQueryDetails,
    isLoadingDetails,
    isErrorDetails,
    handleViewQueryClick,
    handleCloseViewDrawer,
    queriesResponse,
    isLoadingList,
    isErrorList,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleRequestSort,
    handleUpdateStatus,
    handleSendMessage,
    handleUpdateQuery,
    isUpdatingStatus: updateStatusMutation.isPending,
    isSendingMessage: addMessageMutation.isPending,
    isUpdatingQuery: updateQueryMutation.isPending,
  }
}
